import { grantedKeysSatisfy } from '@/lib/authorization/gate-keys';
import surfacesConfig from '@/configs/surfaces/definitions.json';
import businessTypesConfig from '@/configs/business-types/types.json';
import sidebarConfig from '@/configs/navigation/sidebar.json';
import hostsConfig from '@/configs/navigation/hosts.json';

export interface SurfaceDefinition {
  label: string;
  status: 'built' | 'planned';
  scope: 'branch' | 'business' | 'both';
  mount: 'dashboard' | 'pos';
  host: string;
  route: string | null;
  routeAliases?: string[];
  branchRoute?: string;
  permissions: string[];
  ownerOnly: boolean;
  requiresFeature: string | null;
  requiresPosAddon: boolean;
  requiresPosFeature: string | null;
  typeVisibility: Record<string, 'shown' | 'optional' | 'na'>;
  reveal?: { mode?: string; feature?: string; pack?: string | string[] };
  subTypes?: Record<string, 'shown' | 'na'>;
  lanes?: 'any' | string[];
}

export interface PosWorkspaceDefinition {
  label: string;
  posFeature: string;
  permissions: string[];
  lanes: string[];
  revealMode?: string;
}

export interface SubTypeOperationalComposition {
  orderingMode: 'table_first' | 'menu_first' | 'counter_first' | 'custom_build';
  orderFlow: 'standard' | 'quick' | 'build_your_own' | 'pre_order';
  dashboardLayout: 'standard' | 'quick_service' | 'production' | 'fine_dining';
  /**
   * Which order track this sub-type's counter board draws — see
   * `configs/business-types/order-tracks.json` and `lib/order-track.ts`.
   * Undefined means "inherit the type's track, then the config default".
   */
  orderTrack?: string;
  /**
   * How a staff-entered counter sale for this sub-type is fulfilled — the
   * explicit override for `resolveCounterFlow` (lib/pos/counter-flow.ts).
   * Undefined falls back to the order-track default. Unlike `orderTrack` this is
   * an operational policy, not a presentation surface. See doc
   * platform-plan-2026-10/01 §1.1.
   */
  counterFlow?: 'pay_and_go' | 'track_to_handoff';
  primaryActions: string[];
  reports: string[];
}

export interface BusinessTypeSubType {
  label: string;
  presetModes?: string[];
  noDiningRoom?: boolean;
  counterEntry?: boolean;
  recipeExpectation?: string;
  defaultCategories?: string[];
  composition?: {
    posSetup?: { skipSteps?: string[]; archetype?: string; hasDiningRoom?: boolean };
    posEntryWorkspace?: string;
    posWorkspaceLabels?: Record<string, string>;
  };
  vocabulary?: Record<string, string>;
  primaryItemKinds?: string[];
  /**
   * POS lanes this sub-type does not use. Narrowing only — enforced as a subset
   * of the parent type's shown lanes by scripts/check-surface-invariants.mjs.
   * See SubTypeProfile.hiddenWorkspaces in lib/business-types/registry.ts.
   */
  hiddenWorkspaces?: string[];
  operational?: SubTypeOperationalComposition;
}

export interface BusinessTypeDefinition {
  label: string;
  tagline: string;
  status: 'live' | 'coming_soon';
  presetModes: string[];
  assumedEntitlements?: string[];
  posRequirements?: {
    minimumAddon: string;
    requiredFeatures: string[];
    recommendedFeatures: string[];
  };
  vocabulary?: Record<string, string>;
  composition?: {
    posSetup: { skipSteps: string[]; archetype: string; hasDiningRoom: boolean };
    posEntryWorkspace: string;
    posWorkspaceLabels: Record<string, string>;
  };
  presentation?: {
    primaryGroupLabel?: string;
    customerEntry?: string;
    navGroupOrder?: string[] | null;
  };
  subTypes: Record<string, BusinessTypeSubType>;
  quickActions: string[];
  reports: string[];
}

export interface SidebarItem {
  id: string;
  label: string;
  icon: string;
  route: string;
  group: string;
  order: number;
  surface?: string;
  /** Which workspace renders this item. `both` renders in both (doc 05 §3). */
  workspace?: 'business' | 'branch' | 'both';
  requiredPermission?: string[];
  requiresFeature?: string;
  externalRoute?: boolean;
  ownerOnly?: boolean;
  vocabKey?: string;
  posPath?: string;
  /** One-line description, shown in list-style surfaces (mobile More sheet). */
  description?: string;
}

export interface HostDefinition {
  mount: string;
  status: 'live' | 'planned';
  offline: string;
  installable: boolean;
  description: string;
}

// Menu display
const menuDisplayConfig = (surfacesConfig as Record<string, unknown>).menuDisplay as MenuDisplayConfig | undefined;

export type MenuDisplayMode = 'template' | 'web';

export interface MenuDisplayModeConfig {
  label: string;
  description: string;
  default: boolean;
  requiresFeature: string | null;
}

export interface MenuDisplayConfig {
  modes: Record<MenuDisplayMode, MenuDisplayModeConfig>;
  defaultMode: MenuDisplayMode;
}

export function getMenuDisplayConfig(): MenuDisplayConfig {
  return menuDisplayConfig ?? {
    modes: {
      template: {
        label: 'Template Menu',
        description: 'The unified clean menu body with the branch\'s template palette and fonts.',
        default: true,
        requiresFeature: null,
      },
      web: {
        label: 'Web Menu',
        description: 'Legacy value kept for stored data. Renders the same unified menu body as template mode.',
        default: false,
        requiresFeature: null,
      },
    },
    defaultMode: 'template',
  };
}

export function isValidMenuDisplayMode(mode: string): mode is MenuDisplayMode {
  return mode === 'template' || mode === 'web';
}

export function resolveMenuDisplayMode(
  branchMode: string | null | undefined,
  planHasWebFeature: boolean
): MenuDisplayMode {
  if (!branchMode || !isValidMenuDisplayMode(branchMode)) {
    return getMenuDisplayConfig().defaultMode;
  }
  if (branchMode === 'web' && !planHasWebFeature) {
    return 'template';
  }
  return branchMode;
}

// Surfaces
const surfaces = surfacesConfig.surfaces as unknown as Record<string, SurfaceDefinition>;
const posWorkspaces = surfacesConfig.posWorkspaces as unknown as Record<string, PosWorkspaceDefinition>;

// Business types
const businessTypes = businessTypesConfig.businessTypes as unknown as Record<string, BusinessTypeDefinition>;

// Sidebar
const sidebarItems = sidebarConfig.items as unknown as SidebarItem[];
const sidebarGroups = sidebarConfig.groups;
const sidebarStructuralItems = (sidebarConfig as Record<string, unknown>).structuralItems as Record<string, { id: string; label: string; icon: string; route: string; alwaysVisible: boolean; group: string | null; order: number; showOnHosts?: string[]; isChild?: boolean; parentGroup?: string; workspace?: 'business' | 'branch' }> | undefined;
const sidebarGroupOrder = (sidebarConfig as Record<string, unknown>).groupOrder as string[] | undefined;

// Hosts
const hosts = hostsConfig.hosts as unknown as Record<string, HostDefinition>;

export function getSurface(surfaceId: string): SurfaceDefinition | undefined {
  return surfaces[surfaceId];
}

export function getPosWorkspace(workspaceId: string): PosWorkspaceDefinition | undefined {
  return posWorkspaces[workspaceId];
}

export function isSurfaceVisibleForType(surfaceId: string, businessType: string): 'shown' | 'optional' | 'na' | 'not_found' {
  const surface = getSurface(surfaceId);
  if (!surface) return 'not_found';
  return surface.typeVisibility[businessType] ?? 'na';
}

export function resolveVisibleSurfaces(
  businessType: string,
  permissions: string[],
  features: string[],
  isOwner: boolean
): Array<{ surface: string; visibility: 'shown' | 'optional'; disabled: boolean; reason?: string }> {
  const results: Array<{ surface: string; visibility: 'shown' | 'optional'; disabled: boolean; reason?: string }> = [];

  for (const [surfaceId, surface] of Object.entries(surfaces)) {
    const typeVis = surface.typeVisibility[businessType];
    if (typeVis === 'na' || typeVis === undefined) continue;

    if (surface.ownerOnly && !isOwner) continue;

    if (surface.permissions.length > 0) {
      const hasRequired = surface.permissions.some(p => permissions.includes(p));
      if (!hasRequired) continue;
    }

    let disabled = false;
    let reason: string | undefined;

    if (surface.requiresFeature && !features.includes(surface.requiresFeature)) {
      disabled = true;
      reason = `Requires ${surface.requiresFeature}`;
    }

    results.push({
      surface: surfaceId,
      visibility: typeVis,
      disabled,
      reason,
    });
  }

  return results;
}

export function getBusinessType(typeId: string): BusinessTypeDefinition | undefined {
  return businessTypes[typeId];
}

export function getBusinessTypeQuickActions(typeId: string): string[] {
  return getBusinessType(typeId)?.quickActions ?? [];
}

export function getBusinessTypeReports(typeId: string): string[] {
  return getBusinessType(typeId)?.reports ?? [];
}

export function getBusinessTypeSubTypes(typeId: string): Record<string, BusinessTypeSubType> {
  return getBusinessType(typeId)?.subTypes ?? {};
}

export function getBusinessTypePresetModes(typeId: string): string[] {
  return getBusinessType(typeId)?.presetModes ?? [];
}

export function getBusinessTypeVocabulary(typeId: string): Record<string, string> | undefined {
  return getBusinessType(typeId)?.vocabulary;
}

export function getBusinessTypeComposition(typeId: string): BusinessTypeDefinition['composition'] | undefined {
  return getBusinessType(typeId)?.composition;
}

export function getBusinessTypeAssumedEntitlements(typeId: string): string[] {
  return getBusinessType(typeId)?.assumedEntitlements ?? [];
}

export function getBusinessTypePosRequirements(typeId: string): BusinessTypeDefinition['posRequirements'] | undefined {
  return getBusinessType(typeId)?.posRequirements;
}

export function getBusinessTypePresentation(typeId: string): BusinessTypeDefinition['presentation'] | undefined {
  return getBusinessType(typeId)?.presentation;
}

const DEFAULT_OPERATIONAL: SubTypeOperationalComposition = {
  orderingMode: 'table_first',
  orderFlow: 'standard',
  dashboardLayout: 'standard',
  primaryActions: ['menus', 'promotions', 'analytics', 'qr'],
  reports: ['sales_summary', 'menu_engineering', 'labour_cost', 'prime_cost', 'covers_and_ppa'],
};

export function getSubTypeOperationalComposition(
  typeId: string,
  subTypeKey: string | null | undefined,
): SubTypeOperationalComposition {
  if (!subTypeKey) return DEFAULT_OPERATIONAL;
  const bt = getBusinessType(typeId);
  if (!bt) return DEFAULT_OPERATIONAL;
  return bt.subTypes[subTypeKey]?.operational ?? DEFAULT_OPERATIONAL;
}

export function getSubTypeProfile(typeId: string, subTypeLabel: string): BusinessTypeSubType | undefined {
  const bt = getBusinessType(typeId);
  if (!bt) return undefined;
  for (const sub of Object.values(bt.subTypes)) {
    if (sub.label === subTypeLabel) return sub;
  }
  return undefined;
}

// Sidebar
export function getSidebarItems(): SidebarItem[] {
  return [...sidebarItems];
}

export function getSidebarItemsForGroup(group: string): SidebarItem[] {
  return sidebarItems.filter(item => item.group === group).sort((a, b) => a.order - b.order);
}

export function getSidebarItemsForWorkspace(workspace: 'business' | 'branch'): SidebarItem[] {
  // `both`-workspace items render in BOTH workspaces (doc 05 §3), so they belong
  // to either filter — a member whose only business-level access is a dual
  // surface (analytics, feedback, …) still gets the business workspace.
  return sidebarItems
    .filter(item => item.workspace === workspace || item.workspace === 'both')
    .sort((a, b) => a.order - b.order);
}

export function getSidebarItem(itemId: string): SidebarItem | undefined {
  return sidebarItems.find(item => item.id === itemId);
}

export function resolveVisibleSidebarItems(
  permissions: string[],
  features: string[],
  isOwner: boolean,
  host: string
): SidebarItem[] {
  const structuralItems: SidebarItem[] = [];

  const dashItem = (sidebarConfig as any).structuralItems?.dashboard;
  if (dashItem) structuralItems.push(dashItem as SidebarItem);

  const opsItem = (sidebarConfig as any).structuralItems?.operations;
  if (opsItem?.showOnHosts?.includes(host)) structuralItems.push(opsItem as SidebarItem);

  const bpItem = (sidebarConfig as any).structuralItems?.business_profile;
  if (bpItem?.showOnHosts?.includes(host)) structuralItems.push(bpItem as SidebarItem);

  const visibleItems = sidebarItems.filter(item => {
    if (item.ownerOnly && !isOwner) return false;

    if (item.requiredPermission) {
      // A gate may be a legacy permission key or a capability key (F5/P5-02), and
      // `permissions` holds legacy keys. `permissions.includes` denied every
      // re-gated item — the same direct-lookup bug as resolve-surface.ts.
      const hasRequired = item.requiredPermission.some((gate) => grantedKeysSatisfy(permissions, gate));
      if (!hasRequired) return false;
    }

    if (item.requiresFeature && !features.includes(item.requiresFeature)) {
      return false;
    }

    return true;
  });

  return [...structuralItems, ...visibleItems].sort((a, b) => a.order - b.order);
}

export function getNavSurfaces(): string[] {
  return [...(surfacesConfig.navSurfaces as string[])];
}

export function getSurfaceReveal(
  surfaceId: string,
): { mode?: string; feature?: string; pack?: string | string[] } | undefined {
  return getSurface(surfaceId)?.reveal;
}

export function getSurfaceRouteAliases(surfaceId: string): string[] {
  return getSurface(surfaceId)?.routeAliases ?? [];
}

// Hosts
export function getHost(hostId: string): HostDefinition | undefined {
  return hosts[hostId];
}

export function getHostMount(hostId: string): string {
  return getHost(hostId)?.mount ?? '/';
}

export function isHostLive(hostId: string): boolean {
  return getHost(hostId)?.status === 'live';
}

// Sidebar structural items
export function getSidebarStructuralItems(): Record<string, { id: string; label: string; icon: string; route: string; alwaysVisible: boolean; group: string | null; order: number; showOnHosts?: string[]; isChild?: boolean; parentGroup?: string; workspace?: 'business' | 'branch' }> {
  return sidebarStructuralItems ?? {};
}

export function getSidebarGroupOrder(): string[] {
  return sidebarGroupOrder ?? ['catalog', 'branch_catalog', 'growth', 'service', 'bookings', 'commerce', 'staff', 'branch_config', 'insights', 'team', 'workspace', 'branch_settings', 'hotel'];
}

export function getSidebarGroupLabels(): Record<string, string> {
  const labels: Record<string, string> = {};
  if (sidebarGroups && typeof sidebarGroups === 'object') {
    for (const [key, group] of Object.entries(sidebarGroups as Record<string, { label: string }>)) {
      labels[key] = group.label;
    }
  }
  return labels;
}

export function getSidebarGroupConfig(): Record<string, { label: string; workspace?: string }> {
  if (!sidebarGroups || typeof sidebarGroups !== 'object') return {};
  return sidebarGroups as Record<string, { label: string; workspace?: string }>;
}

export function getInstallableHosts(): string[] {
  return Object.entries(hosts)
    .filter(([, h]) => h.installable)
    .map(([id]) => id);
}

// Panels
export interface PanelConfig {
  parent?: string;
  host?: string;
  label: string;
  status: string;
  scope: string;
  permissions: string[];
  ownerOnly?: boolean;
  requiresFeature?: string;
  requiresPosFeature?: string;
  revealMode?: string;
}

const panels = (surfacesConfig as Record<string, unknown>).panels as Record<string, PanelConfig> | undefined;

export function getPanels(): Record<string, PanelConfig> {
  return panels ?? {};
}

export function getPanel(panelId: string): PanelConfig | undefined {
  return panels?.[panelId];
}

export {
  surfaces as allSurfaces,
  posWorkspaces as allPosWorkspaces,
  businessTypes as allBusinessTypes,
  sidebarItems as allSidebarItems,
  sidebarGroups as allSidebarGroups,
  hosts as allHosts,
};

const btConfig = businessTypesConfig as Record<string, unknown>;

/** Nav surfaces from config (was NAV_SURFACES in registry.ts). */
export const navSurfaces: readonly string[] = (btConfig.navSurfaces as string[]) ?? [];

/** Home action keys from config (was HOME_ACTION_KEYS in registry.ts). */
export const homeActionKeys: readonly string[] = (btConfig.homeActionKeys as string[]) ?? [];

/** Nav group order from config (was NAV_GROUPS in registry.ts). */
export const navGroups: readonly string[] = (btConfig.navGroups as string[]) ?? [];

/** Report keys from config (was REPORT_KEYS in registry.ts). */
export const reportKeys: readonly string[] = (btConfig.reportKeys as string[]) ?? [];
